import { useHeaderContext } from '@portal/state/context/HeaderContext';
import { routes } from 'constants/routes';
import Head from 'next/head';
import { useIsShopPageAvailable } from 'processes/ShopV2/hooks/useIsShopPageAvailable';
import { useLocale } from 'shared/hooks/useLocale';
import Nav from 'shared/ui/Nav/Nav';
import { useQueryGetCommonContentData } from 'state/studio/commonContentData/getCommonContentData';
import { useIsEnabled } from '../hooks/useIsEnabled';
import { NavLink } from '../ui/Nav/Nav.types';
import { Feature } from '../utils/featureToggle';

type LayoutProps = {
  children: any;
  title?: string;
  isLoading?: boolean;
  className?: string;
};

function isLink(
  link: { id: string; title: string | undefined; path: string | undefined } | undefined
): link is NavLink {
  return Boolean(link?.id && link?.title && link?.path);
}

function useLinks() {
  const { isShopPageAvailable, isLoading } = useIsShopPageAvailable();
  const isReferFriendsEnabled = useIsEnabled(Feature.referFriends);
  const { headerData } = useHeaderContext();
  const locale = useLocale();
  const commonContent = useQueryGetCommonContentData({ locale }).data;
  const navRedesignSubmenu = headerData.header?.navV2MemberSubmenu;

  if (navRedesignSubmenu && navRedesignSubmenu.length > 0) {
    if (!isLoading) {
      return navRedesignSubmenu
        .filter((link) => {
          if (link.navLink === routes.shop()) {
            return isShopPageAvailable;
          }
          if (link.navLink === routes.refFriend) {
            return isReferFriendsEnabled;
          }
          return link.text && link.navLink;
        })
        .map((link) => ({ id: link._key, title: link.text, path: link.navLink }));
    }
  } else if (commonContent) {
    const CONTENT = commonContent.tabs;
    return [
      { id: 'subscriptions', title: CONTENT.subscriptions, path: routes.home },
      isShopPageAvailable ? { id: 'shop', title: CONTENT.shop, path: routes.shop() } : undefined,
      { id: 'orders', title: CONTENT.orders, path: routes.ordersHistory },
      isReferFriendsEnabled
        ? { id: 'refer-friends', title: CONTENT.friends, path: routes.refFriend }
        : undefined,
      { id: 'settings', title: CONTENT.settings, path: routes.settings }
    ].filter(isLink);
  }

  return [];
}

const Layout = ({ children, title, isLoading, className }: LayoutProps) => {
  const pageTitle = title || 'AG1 by Athletic Greens® - Official Site';
  const links = useLinks();

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <main className={className}>
        <Nav links={links.filter(isLink)} isLoading={isLoading} />
        {children}
      </main>
    </>
  );
};

export default Layout;
